import { useState, useEffect, useMemo } from 'react';
import { API, formatDateTime, formatWeight, displayDate } from '@/lib/api';
import { useSubmitGuard } from '@/lib/useSubmitGuard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';

export default function ApprovalsPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const [approvals, setApprovals] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('pending');
  const [actionDialog, setActionDialog] = useState(null);
  const [comment, setComment] = useState('');
  const { busy: loading, run } = useSubmitGuard('Please wait — the previous action is still processing');
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const PAGE_SIZE = 200;

  const fetchApprovals = async (pageToLoad = 1, append = false) => {
    try {
      const params = { status: filter !== 'all' ? filter : undefined, page: pageToLoad, page_size: PAGE_SIZE };
      const { data } = await API.get('/approvals', { params });
      setApprovals(prev => append ? [...prev, ...data.approvals] : data.approvals);
      setTotal(data.total);
      setHasMore(data.has_more);
      setPage(pageToLoad);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { fetchApprovals(1, false); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = () => fetchApprovals(page + 1, true);

  const handleAction = async (action) => {
    if (!actionDialog) return;
    return run(async () => {
      try {
        await API.post(`/approvals/${actionDialog.id}/action`, { action, approved_by: user?.name || user?.role || 'admin', comments: comment });
        toast.success(`Approval ${action}`);
        setActionDialog(null); setComment('');
        fetchApprovals();
      } catch(err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    });
  };

  const handleApproveAll = async () => {
    const pendingCount = filter === 'pending' ? total : approvals.filter(a => a.status === 'pending').length;
    if (!pendingCount) { toast.info('No pending approvals'); return; }
    if (!window.confirm(`Approve all ${pendingCount} pending approvals?`)) return;
    return run(async () => {
      try {
        const res = await API.post('/approvals/approve-all', { action: 'approved', approved_by: user?.name || user?.role || 'admin', comments: 'Bulk approved' });
        toast.success(res.data.message || 'All approvals approved');
        fetchApprovals();
      } catch(err) { toast.error(err.response?.data?.detail || 'Bulk approve failed'); }
    });
  };

  const handleDelete = async (approvalId) => {
    if (!window.confirm('Delete this approval entry? This cannot be undone.')) return;
    return run(async () => {
      try {
        await API.delete(`/approvals/${approvalId}`);
        toast.success('Approval deleted');
        fetchApprovals();
      } catch(err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    });
  };

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle size={14} className="text-emerald-600" />;
    if (status === 'rejected') return <XCircle size={14} className="text-rose-600" />;
    return <Clock size={14} className="text-amber-600" />;
  };

  const pendingCount = filter === 'pending' ? total : approvals.filter(a => a.status === 'pending').length;

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortedApprovals = useMemo(() => {
    const sorted = [...approvals];
    sorted.sort((a, b) => {
      let va, vb;
      if (sortCol === 'created_at') {
        va = a.created_at || ''; vb = b.created_at || '';
      } else if (sortCol === 'type') {
        va = a.approval_type || ''; vb = b.approval_type || '';
      } else if (sortCol === 'submitted_by') {
        va = a.submitted_by || ''; vb = b.submitted_by || '';
      } else if (sortCol === 'status') {
        va = a.status || ''; vb = b.status || '';
      } else if (sortCol === 'box_id') {
        va = a.details?.box_id || ''; vb = b.details?.box_id || '';
      } else if (sortCol === 'gross') {
        va = a.details?.gross_weight_g || 0; vb = b.details?.gross_weight_g || 0;
        return sortDir === 'asc' ? va - vb : vb - va;
      } else if (sortCol === 'gold') {
        va = a.details?.gold_weight_g || 0; vb = b.details?.gold_weight_g || 0;
        return sortDir === 'asc' ? va - vb : vb - va;
      } else {
        va = ''; vb = '';
      }
      va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return sorted;
  }, [approvals, sortCol, sortDir]);

  const SortHeader = ({ col, label, className }) => (
    <TableHead className={`cursor-pointer select-none ${className || ''}`} onClick={() => toggleSort(col)}>
      <div className="flex items-center gap-1">
        {label}
        {sortCol === col ? (sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={12} className="opacity-30" />}
      </div>
    </TableHead>
  );

  return (
    <div data-testid="approvals-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div><h1>Approvals</h1><p className="hidden sm:block">Review and approve pending actions</p></div>
        {filter === 'pending' && pendingCount > 0 && (
          <Button variant="outline" onClick={handleApproveAll} disabled={loading} data-testid="approvals-approve-all-button"
            className="w-full sm:w-auto text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10">
            <CheckCircle size={16} className="mr-2" />Approve All ({pendingCount})
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4 overflow-x-auto">
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} className="text-xs sm:text-sm whitespace-nowrap" data-testid={`approvals-filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Mobile: Card list */}
      <div className="mobile-card-list">
        {sortedApprovals.map(a => (
          <Card key={a.id}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <Badge variant="outline" className="text-xs">{(a.approval_type || '').replace(/_/g, ' ')}</Badge>
                <div className="flex items-center gap-2">
                  <span className={`status-badge status-${a.status} text-xs`}>{statusIcon(a.status)} {a.status}</span>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(a.id)} data-testid={`approval-delete-mobile-${a.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              </div>
              <div className="text-xs space-y-1">
                {a.details?.box_id && <p>Box: <b className="font-mono">{a.details.box_id}</b></p>}
                {a.details?.gold_purity && <p>Purity: <b>{a.details.gold_purity}</b></p>}
                {a.details?.gross_weight_g != null && <p>Gross: <b>{a.details.gross_weight_g}g</b></p>}
                {a.details?.gold_weight_g != null && <p>Gold: <b>{a.details.gold_weight_g}g</b></p>}
                {a.details?.date && <p>Date: {displayDate(a.details.date)}</p>}
                <p className="text-muted-foreground">By: {a.submitted_by} | {formatDateTime(a.created_at)}</p>
              </div>
              {a.status === 'pending' && (
                <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => setActionDialog(a)} data-testid="approval-row-approve-button">Review</Button>
              )}
            </CardContent>
          </Card>
        ))}
        {approvals.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No {filter !== 'all' ? filter : ''} approvals</p>}
      </div>

      {/* Desktop: Table */}
      <div className="desktop-table">
        <Card>
          <CardContent className="p-0">
            <div className="table-scroll-wrapper">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <SortHeader col="type" label="Type" />
                    <SortHeader col="box_id" label="Details" />
                    <SortHeader col="gross" label="Gross" className="text-right" />
                    <SortHeader col="gold" label="Gold" className="text-right" />
                    <SortHeader col="submitted_by" label="By" />
                    <SortHeader col="created_at" label="Date" />
                    <SortHeader col="status" label="Status" />
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedApprovals.map(a => (
                    <TableRow key={a.id}>
                      <TableCell><Badge variant="outline">{(a.approval_type || '').replace(/_/g, ' ')}</Badge></TableCell>
                      <TableCell className="max-w-xs">
                        <div className="text-sm">
                          {a.details?.box_id && <span>Box: <b className="font-mono">{a.details.box_id}</b> </span>}
                          {a.details?.gold_purity && <span className={`status-badge purity-${a.details.gold_purity} text-xs ml-1`}>{a.details.gold_purity}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right weight-value text-sm">{a.details?.gross_weight_g != null ? `${a.details.gross_weight_g}g` : '-'}</TableCell>
                      <TableCell className="text-right weight-value text-sm">{a.details?.gold_weight_g != null ? `${a.details.gold_weight_g}g` : '-'}</TableCell>
                      <TableCell className="text-sm">{a.submitted_by}</TableCell>
                      <TableCell className="text-sm">{formatDateTime(a.created_at)}</TableCell>
                      <TableCell><span className={`status-badge status-${a.status}`}>{statusIcon(a.status)} {a.status}</span></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {a.status === 'pending' && (
                            <Button variant="outline" size="sm" onClick={() => setActionDialog(a)} data-testid="approval-row-approve-button">Review</Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(a.id)} data-testid={`approval-delete-${a.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {approvals.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No {filter !== 'all' ? filter : ''} approvals</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {approvals.length > 0 && (
        <div className="flex flex-col items-center gap-2 py-4" data-testid="approvals-pagination-footer">
          <span className="text-xs text-muted-foreground font-mono" data-testid="approvals-shown-count">Showing {approvals.length} of {total}</span>
          {hasMore && (
            <Button variant="outline" size="sm" onClick={loadMore} data-testid="approvals-load-more-btn">Load more</Button>
          )}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Review Approval</DialogTitle></DialogHeader>
          {actionDialog && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <p><b>Type:</b> {(actionDialog.approval_type || '').replace(/_/g, ' ')}</p>
                <p><b>By:</b> {actionDialog.submitted_by}</p>
                {actionDialog.details?.box_id && <p><b>Box:</b> <span className="font-mono">{actionDialog.details.box_id}</span></p>}
                {actionDialog.details?.gold_purity && <p><b>Purity:</b> {actionDialog.details.gold_purity}</p>}
                {actionDialog.details?.gross_weight_g != null && <p><b>Gross:</b> {actionDialog.details.gross_weight_g}g</p>}
                {actionDialog.details?.gold_weight_g != null && <p><b>Gold:</b> {actionDialog.details.gold_weight_g}g</p>}
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Comments (required for rejection)</label>
                <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Enter rejection reason or comments..." rows={3} />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button className="flex-1" onClick={() => handleAction('approved')} disabled={loading} data-testid="approval-approve-btn"><CheckCircle size={16} className="mr-1" /> Approve</Button>
                <Button variant="destructive" className="flex-1" onClick={() => handleAction('rejected')} disabled={loading} data-testid="approval-reject-btn"><XCircle size={16} className="mr-1" /> Reject</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
